// Humanoid rig factory — builds the 13-body / 12-joint ragdoll described in
// humanoidBones.ts on a box3d-web World. No `three` import (Node-testable).
//
// T-pose spawn layout, built bottom-up in a CANONICAL frame (feet resting on
// y=0, hips on the vertical centerline, character facing +Z) then placed into
// the world by rotating every canonical position/orientation by `spec.rotation`
// and translating by `spec.position`. "Left"/"right" are -X/+X in this frame.
import type { BodyHandle, JointHandle, ShapeHandle, TransformBuffer, World } from 'box3d-web';
import {
  HUMANOID_BONE_NAMES,
  HUMANOID_JOINT_NAMES,
  anatomicalFootHeelBiasM,
  boneMassKg,
  boneShapeVolumeM3,
  isSphericalLimitRad,
  jointLimitRadiansForType,
  resolveJointDefs,
  scaledBoneShapeDims,
  type BoneShapeDims,
  type HumanoidBoneName,
  type HumanoidJointDef,
  type HumanoidJointName,
  type RevoluteLimitRad,
  type SphericalLimitRad,
} from './humanoidBones';
import {
  resolveHumanoidSpec,
  type HumanoidSpec,
  type HumanoidSpecInput,
  type QuatTuple,
  type Vec3Tuple,
} from './humanoidSpec';

export interface Humanoid {
  readonly spec: HumanoidSpec;
  /**
   * Actual joint defs used to build this rig (layout-resolved). Motor/eval
   * code must use these — not the global HUMANOID_JOINT_DEFS table — so
   * muscleBalance spherical ankles are not treated as revolute.
   */
  readonly jointDefs: readonly HumanoidJointDef[];
  readonly bodies: Readonly<Record<HumanoidBoneName, BodyHandle>>;
  readonly shapes: Readonly<Record<HumanoidBoneName, ShapeHandle>>;
  readonly joints: Readonly<Record<HumanoidJointName, JointHandle>>;
  /** World-space anchor point of each joint AT SPAWN (before any stepping).
   *  Combined with each body's spawn transform this lets evaluation code
   *  recompute per-body local anchor offsets without a box3d-web joint-anchor
   *  accessor (not part of the frozen v0.1 API). */
  readonly jointAnchors: Readonly<Record<HumanoidJointName, Vec3Tuple>>;
  /** World-space hinge axis AT SPAWN for revolute joints only (undefined for
   *  spherical joints, which have no axis concept). */
  readonly jointAxes: Readonly<Partial<Record<HumanoidJointName, Vec3Tuple>>>;
  /** Alias for `bodies.hips` — the body every other bone chains from. */
  readonly root: BodyHandle;
  /** Register all 13 bodies (HUMANOID_BONE_NAMES order) on a TransformBuffer. */
  track(buffer: TransformBuffer): void;
  /** Destroys all 12 joints, THEN all 13 bodies, restoring the world's body count. */
  destroy(world: World): void;
}

export interface HumanoidCrowdOptions {
  count?: number;
  spec?: HumanoidSpecInput;
  layout?: 'grid' | 'line';
  seedBase?: number;
  /** Fraction (0..1) of uniform height jitter per instance. Overrides spec.scaleJitter. */
  scaleJitter?: number;
  spacing?: number;
}

// --- per-bone angular damping multiplier (relative to spec.angularDamping) ---
// Distal/small segments (feet, forearms) get extra damping to settle without
// residual micro-wobble; the torso stays close to the spec base value.
// Measured against evaluateHumanoidPhysics's settle gate: the head, being the
// last link in the hips->spine->head chain with no further children to bleed
// energy into, was the slowest to decay (still ~0.4 rad/s at t=5s with a 1.0x
// multiplier) — bumped well above the others rather than loosening the
// settle-gate threshold.
const DAMPING_MULTIPLIERS: Readonly<Record<HumanoidBoneName, number>> = {
  hips: 0.8,
  spine: 1.0,
  head: 4.0,
  leftUpperArm: 1.4,
  rightUpperArm: 1.4,
  leftLowerArm: 1.8,
  rightLowerArm: 1.8,
  leftUpperLeg: 1.1,
  rightUpperLeg: 1.1,
  leftLowerLeg: 1.3,
  rightLowerLeg: 1.3,
  leftFoot: 1.9,
  rightFoot: 1.9,
};

const LEG_X_FRACTION = 0.55; // fraction of hips half-width (default / certified feet)
/**
 * Anatomical feet are only ~10 cm wide (vs legacy 26 cm lateral). Slightly
 * wider inter-leg stance keeps a plausible adult biped base without changing
 * foot volume/mass — muscleBalance-only via footLayout.
 */
const ANATOMICAL_LEG_X_FRACTION = 0.82;
const SHOULDER_GAP_M = 0.02;
const SHOULDER_DROP_FRACTION = 1.0; // shoulder joint sits `spine radius` below spine top

interface CanonicalBoneNode {
  position: Vec3Tuple;
  rotation: QuatTuple;
  dims: BoneShapeDims;
}
interface CanonicalJointNode {
  anchor: Vec3Tuple;
  axis?: Vec3Tuple;
}
interface CanonicalLayout {
  bones: Record<HumanoidBoneName, CanonicalBoneNode>;
  joints: Record<HumanoidJointName, CanonicalJointNode>;
}

const IDENTITY_QUAT: QuatTuple = [0, 0, 0, 1];

export function quatAroundZ(angleRad: number): QuatTuple {
  return [0, 0, Math.sin(angleRad / 2), Math.cos(angleRad / 2)];
}

export function quatAroundY(angleRad: number): QuatTuple {
  return [0, Math.sin(angleRad / 2), 0, Math.cos(angleRad / 2)];
}

export function multiplyQuat(a: QuatTuple, b: QuatTuple): QuatTuple {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

export function conjugateQuat(q: QuatTuple): QuatTuple {
  return [-q[0], -q[1], -q[2], q[3]];
}

export function rotateVectorByQuat(q: QuatTuple, v: Vec3Tuple): Vec3Tuple {
  const [qx, qy, qz, qw] = q;
  const [vx, vy, vz] = v;
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return [
    vx + qw * tx + (qy * tz - qz * ty),
    vy + qw * ty + (qz * tx - qx * tz),
    vz + qw * tz + (qx * ty - qy * tx),
  ];
}

function addVec3(a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function capsuleHalfLength(dims: Extract<BoneShapeDims, { kind: 'capsule' }>): number {
  return dims.halfHeightM + dims.radiusM;
}

/** Builds the T-pose layout in the canonical (feet-on-ground) frame. */
function computeCanonicalLayout(spec: HumanoidSpec): CanonicalLayout {
  const dimsOf = (bone: HumanoidBoneName) =>
    scaledBoneShapeDims(bone, spec.height, spec.proportionPreset, spec.footLayout);

  const hipsDims = dimsOf('hips');
  const spineDims = dimsOf('spine');
  const headDims = dimsOf('head');
  const upperArmDimsL = dimsOf('leftUpperArm');
  const upperArmDimsR = dimsOf('rightUpperArm');
  const lowerArmDimsL = dimsOf('leftLowerArm');
  const lowerArmDimsR = dimsOf('rightLowerArm');
  const upperLegDimsL = dimsOf('leftUpperLeg');
  const upperLegDimsR = dimsOf('rightUpperLeg');
  const lowerLegDimsL = dimsOf('leftLowerLeg');
  const lowerLegDimsR = dimsOf('rightLowerLeg');
  const footDimsL = dimsOf('leftFoot');
  const footDimsR = dimsOf('rightFoot');
  if (hipsDims.kind !== 'box' || footDimsL.kind !== 'box' || footDimsR.kind !== 'box') {
    throw new RangeError('humanoid layout expects box shapes for hips/feet.');
  }
  if (spineDims.kind !== 'capsule' || headDims.kind !== 'sphere') {
    throw new RangeError('humanoid layout expects a capsule spine and a sphere head.');
  }

  // Relative-to-hips-center Y coordinates (hips center = 0). Legs/feet hang
  // below (negative), spine/head rise above (positive).
  const hipsBottom = -hipsDims.halfExtentsM[1];
  const hipsTop = hipsDims.halfExtentsM[1];

  const buildLeg = (upperLegDims: BoneShapeDims, lowerLegDims: BoneShapeDims, footDims: BoneShapeDims) => {
    if (upperLegDims.kind !== 'capsule' || lowerLegDims.kind !== 'capsule' || footDims.kind !== 'box') {
      throw new RangeError('humanoid leg layout expects capsule upper/lower legs and a box foot.');
    }
    const hipJointY = hipsBottom;
    const upperLegHalf = capsuleHalfLength(upperLegDims);
    const upperLegCenterY = hipJointY - upperLegHalf;
    const kneeJointY = hipJointY - 2 * upperLegHalf;
    const lowerLegHalf = capsuleHalfLength(lowerLegDims);
    const lowerLegCenterY = kneeJointY - lowerLegHalf;
    const ankleJointY = kneeJointY - 2 * lowerLegHalf;
    const footCenterY = ankleJointY - footDims.halfExtentsM[1];
    const footBottomY = ankleJointY - 2 * footDims.halfExtentsM[1];
    return {
      hipJointY,
      upperLegCenterY,
      kneeJointY,
      lowerLegCenterY,
      ankleJointY,
      footCenterY,
      footBottomY,
    };
  };

  const legL = buildLeg(upperLegDimsL, lowerLegDimsL, footDimsL);
  const legR = buildLeg(upperLegDimsR, lowerLegDimsR, footDimsR);
  const groundShift = -Math.min(legL.footBottomY, legR.footBottomY);

  // Anatomical feet: shift foot CoM forward so the ankle sits near the heel
  // while the support polygon extends under the toes. Ankle anchors stay at z=0.
  const footHeelBiasZ =
    spec.footLayout === 'anatomical'
      ? anatomicalFootHeelBiasM(spec.height, spec.proportionPreset)
      : 0;

  const spineHalf = capsuleHalfLength(spineDims);
  const spineCenterY = hipsTop + spineHalf;
  const spineTopY = hipsTop + 2 * spineHalf;
  const headCenterY = spineTopY + headDims.radiusM;

  const shoulderJointY = spineTopY - spineDims.radiusM * SHOULDER_DROP_FRACTION;
  const shoulderX = spineDims.radiusM + SHOULDER_GAP_M;
  const legXFraction =
    spec.footLayout === 'anatomical' ? ANATOMICAL_LEG_X_FRACTION : LEG_X_FRACTION;
  const legX = hipsDims.halfExtentsM[0] * legXFraction;

  const buildArm = (
    upperArmDims: BoneShapeDims,
    lowerArmDims: BoneShapeDims,
    sign: -1 | 1,
  ) => {
    if (upperArmDims.kind !== 'capsule' || lowerArmDims.kind !== 'capsule') {
      throw new RangeError('humanoid arm layout expects capsule upper/lower arms.');
    }
    const shoulderJointX = sign * shoulderX;
    const upperArmHalf = capsuleHalfLength(upperArmDims);
    const upperArmCenterX = shoulderJointX + sign * upperArmHalf;
    const elbowJointX = shoulderJointX + sign * 2 * upperArmHalf;
    const lowerArmHalf = capsuleHalfLength(lowerArmDims);
    const lowerArmCenterX = elbowJointX + sign * lowerArmHalf;
    return { shoulderJointX, upperArmCenterX, elbowJointX, lowerArmCenterX };
  };

  const armL = buildArm(upperArmDimsL, lowerArmDimsL, -1);
  const armR = buildArm(upperArmDimsR, lowerArmDimsR, 1);

  const armRotationL = quatAroundZ(Math.PI / 2); // points -X
  const armRotationR = quatAroundZ(-Math.PI / 2); // points +X

  const y = (value: number) => value + groundShift;

  const bones: Record<HumanoidBoneName, CanonicalBoneNode> = {
    hips: { position: [0, y(0), 0], rotation: IDENTITY_QUAT, dims: hipsDims },
    spine: { position: [0, y(spineCenterY), 0], rotation: IDENTITY_QUAT, dims: spineDims },
    head: { position: [0, y(headCenterY), 0], rotation: IDENTITY_QUAT, dims: headDims },
    leftUpperArm: { position: [armL.upperArmCenterX, y(shoulderJointY), 0], rotation: armRotationL, dims: upperArmDimsL },
    rightUpperArm: { position: [armR.upperArmCenterX, y(shoulderJointY), 0], rotation: armRotationR, dims: upperArmDimsR },
    leftLowerArm: { position: [armL.lowerArmCenterX, y(shoulderJointY), 0], rotation: armRotationL, dims: lowerArmDimsL },
    rightLowerArm: { position: [armR.lowerArmCenterX, y(shoulderJointY), 0], rotation: armRotationR, dims: lowerArmDimsR },
    leftUpperLeg: { position: [-legX, y(legL.upperLegCenterY), 0], rotation: IDENTITY_QUAT, dims: upperLegDimsL },
    rightUpperLeg: { position: [legX, y(legR.upperLegCenterY), 0], rotation: IDENTITY_QUAT, dims: upperLegDimsR },
    leftLowerLeg: { position: [-legX, y(legL.lowerLegCenterY), 0], rotation: IDENTITY_QUAT, dims: lowerLegDimsL },
    rightLowerLeg: { position: [legX, y(legR.lowerLegCenterY), 0], rotation: IDENTITY_QUAT, dims: lowerLegDimsR },
    leftFoot: {
      position: [-legX, y(legL.footCenterY), footHeelBiasZ],
      rotation: IDENTITY_QUAT,
      dims: footDimsL,
    },
    rightFoot: {
      position: [legX, y(legR.footCenterY), footHeelBiasZ],
      rotation: IDENTITY_QUAT,
      dims: footDimsR,
    },
  };

  const joints: Record<HumanoidJointName, CanonicalJointNode> = {
    spine: { anchor: [0, y(hipsTop), 0] },
    neck: { anchor: [0, y(spineTopY), 0] },
    leftShoulder: { anchor: [armL.shoulderJointX, y(shoulderJointY), 0] },
    rightShoulder: { anchor: [armR.shoulderJointX, y(shoulderJointY), 0] },
    leftElbow: { anchor: [armL.elbowJointX, y(shoulderJointY), 0], axis: [0, 0, 1] },
    rightElbow: { anchor: [armR.elbowJointX, y(shoulderJointY), 0], axis: [0, 0, 1] },
    leftHip: { anchor: [-legX, y(legL.hipJointY), 0] },
    rightHip: { anchor: [legX, y(legR.hipJointY), 0] },
    leftKnee: { anchor: [-legX, y(legL.kneeJointY), 0], axis: [1, 0, 0] },
    rightKnee: { anchor: [legX, y(legR.kneeJointY), 0], axis: [1, 0, 0] },
    leftAnkle: { anchor: [-legX, y(legL.ankleJointY), 0], axis: [1, 0, 0] },
    rightAnkle: { anchor: [legX, y(legR.ankleJointY), 0], axis: [1, 0, 0] },
  };

  return { bones, joints };
}

export function createHumanoid(world: World, specInput: HumanoidSpecInput = {}): Humanoid {
  const spec = resolveHumanoidSpec(specInput);
  const layout = computeCanonicalLayout(spec);

  const bodies = {} as Record<HumanoidBoneName, BodyHandle>;
  const shapes = {} as Record<HumanoidBoneName, ShapeHandle>;

  for (const bone of HUMANOID_BONE_NAMES) {
    const node = layout.bones[bone];
    const worldPosition = addVec3(spec.position, rotateVectorByQuat(spec.rotation, node.position));
    const worldRotation = multiplyQuat(spec.rotation, node.rotation);
    const body = world.createBody({
      type: 'dynamic',
      position: worldPosition,
      rotation: worldRotation,
      linearDamping: spec.linearDamping,
      angularDamping: spec.angularDamping * DAMPING_MULTIPLIERS[bone],
    });
    const massKg = boneMassKg(bone, spec.mass);
    const volumeM3 = boneShapeVolumeM3(node.dims);
    const density = massKg / volumeM3;
    const material = { density, friction: spec.friction, restitution: spec.restitution };
    const dims = node.dims;
    shapes[bone] =
      dims.kind === 'box'
        ? world.addBox(body, dims.halfExtentsM, material)
        : dims.kind === 'sphere'
          ? world.addSphere(body, dims.radiusM, material)
          : world.addCapsule(body, dims.radiusM, dims.halfHeightM, material);
    bodies[bone] = body;
  }

  const jointDefs = resolveJointDefs(spec.jointLayout);
  const joints = {} as Record<HumanoidJointName, JointHandle>;
  const jointAnchors = {} as Record<HumanoidJointName, Vec3Tuple>;
  const jointAxes: Partial<Record<HumanoidJointName, Vec3Tuple>> = {};
  for (const def of jointDefs) {
    const node = layout.joints[def.name];
    const worldAnchor = addVec3(spec.position, rotateVectorByQuat(spec.rotation, node.anchor));
    jointAnchors[def.name] = worldAnchor;
    const tuning = spec.jointTuning[def.name];
    if (def.type === 'spherical') {
      const limit = jointLimitRadiansForType(spec.limitPreset, def.name, def.type) as SphericalLimitRad;
      if (!isSphericalLimitRad(limit)) throw new RangeError(`Expected spherical limit for ${def.name}.`);
      joints[def.name] = world.createSphericalJoint(bodies[def.parent], bodies[def.child], {
        anchor: worldAnchor,
        coneLimit: limit.coneRad,
        // Shoulders omit twistLimit (API: "omit = unlimited"). Diagnosed via
        // scripts/_diag-firststep.ts: box3d-web's spherical-joint twist reads
        // against each body's rotation as created, not a relative-to-spawn
        // reference the way the revolute joints behave — so the T-pose's
        // ~90deg upperArm rotation (needed to lay the Y-aligned capsule out
        // along +/-X) already reads as a near-90deg twist from frame one,
        // and a +/-60deg twistLimit immediately fought a "violation" that
        // is really just our spawn geometry, injecting energy (measured:
        // lower-arm angular speed to 24 rad/s in the very first fixed step,
        // even with gravity off and no floor). coneLimit alone was confirmed
        // stable in isolation, so shoulders keep swing-limited without twist.
        // Ankles (muscleBalance spherical) keep near-zero twist — spawn at
        // identity, so twistLimit is valid.
        ...(def.name !== 'leftShoulder' && def.name !== 'rightShoulder' ? { twistLimit: limit.twistRad } : {}),
        ...(tuning?.springHertz
          ? { spring: { hertz: tuning.springHertz, dampingRatio: tuning.springDampingRatio } }
          : {}),
      });
    } else {
      const limit = jointLimitRadiansForType(spec.limitPreset, def.name, def.type) as RevoluteLimitRad;
      if (!node.axis) throw new RangeError(`Missing revolute axis for ${def.name}.`);
      const worldAxis = rotateVectorByQuat(spec.rotation, node.axis);
      jointAxes[def.name] = worldAxis;
      joints[def.name] = world.createRevoluteJoint(bodies[def.parent], bodies[def.child], {
        anchor: worldAnchor,
        axis: worldAxis,
        limit: [limit.loRad, limit.hiRad],
      });
    }
  }

  return {
    spec,
    jointDefs,
    bodies,
    shapes,
    joints,
    jointAnchors,
    jointAxes,
    root: bodies.hips,
    track(buffer: TransformBuffer) {
      for (const bone of HUMANOID_BONE_NAMES) buffer.add(bodies[bone]);
    },
    destroy(w: World) {
      for (const name of HUMANOID_JOINT_NAMES) w.destroyJoint(joints[name]);
      for (const bone of HUMANOID_BONE_NAMES) w.destroyBody(bodies[bone]);
    },
  };
}

function seededUnit(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Deterministic crowd spawn: grid or line layout with sin-hash position/yaw/
 *  height jitter (same `seededUnit` pattern as yardPhysics). */
export function createHumanoidCrowd(world: World, options: HumanoidCrowdOptions = {}): Humanoid[] {
  const count = Math.max(0, Math.floor(options.count ?? 1));
  const baseSpec = options.spec ?? {};
  const layoutKind = options.layout ?? 'grid';
  const seedBase = options.seedBase ?? 0;
  const scaleJitter = options.scaleJitter ?? baseSpec.scaleJitter ?? 0;
  const spacing = options.spacing ?? 1.2;
  const columns = Math.max(1, Math.ceil(Math.sqrt(count)));
  const basePosition: Vec3Tuple = baseSpec.position ? [...baseSpec.position] as Vec3Tuple : [0, 0, 0];
  const baseHeight = baseSpec.height ?? 1.8;

  const humanoids: Humanoid[] = [];
  for (let i = 0; i < count; i += 1) {
    const col = i % columns;
    const row = Math.floor(i / columns);
    const gridX = layoutKind === 'line' ? i * spacing : (col - (columns - 1) / 2) * spacing;
    const gridZ = layoutKind === 'line' ? 0 : row * spacing;
    const seed = seedBase + i;
    const jitterX = (seededUnit(seed * 3.1 + 1) - 0.5) * spacing * 0.3;
    const jitterZ = (seededUnit(seed * 7.7 + 2) - 0.5) * spacing * 0.3;
    const yaw = seededUnit(seed * 11.3 + 3) * Math.PI * 2;
    const heightJitter = 1 + (seededUnit(seed * 5.9 + 4) - 0.5) * 2 * scaleJitter;
    const spec: HumanoidSpecInput = {
      ...baseSpec,
      height: baseHeight * heightJitter,
      position: [basePosition[0] + gridX + jitterX, basePosition[1], basePosition[2] + gridZ + jitterZ],
      rotation: quatAroundY(yaw),
      seed,
    };
    humanoids.push(createHumanoid(world, spec));
  }
  return humanoids;
}
